import React, { useEffect, useState } from 'react';
import { Skeleton } from 'antd';
import {
  apiGetDeviceStatisticsByBranch,
  apiGetCustomerStatisticsByDeviceInstallation,
  apiGetWarningStatisticsByType
} from '@services/ManageDevices/DeviceServices';
import { formatCurrency } from '@utils/utils';
import moment from 'moment';
import LineIcon from './LineIcon';

// Same mechanism the status donuts use: scroll to the device list and filter it
const filterDeviceList = (status) => {
  document.getElementById('device-list-table')?.scrollIntoView({ behavior: 'smooth' });
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent('filterDeviceStatus', { detail: { status } }));
  }, 500);
};

const scrollToTriage = () =>
  document.getElementById('dash-triage')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

const toInt = (value) => parseInt(value || 0, 10) || 0;
const percent = (part, total) => (total ? Math.round((part / total) * 1000) / 10 : 0);

const StatisticsCards = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  const fetchStatistics = async () => {
    const [deviceRes, customerRes, warningRes] = await Promise.allSettled([
      apiGetDeviceStatisticsByBranch(),
      apiGetCustomerStatisticsByDeviceInstallation(),
      apiGetWarningStatisticsByType({ date: moment().format('YYYY-MM-DD') })
    ]);

    // Prefer the API's top-level totals; fall back to summing branches if absent.
    const deviceData = deviceRes.status === 'fulfilled' ? deviceRes.value.data?.data : null;
    const branches = Array.isArray(deviceData?.branches) ? deviceData.branches : [];
    const sumBranches = (field) => branches.reduce((sum, b) => sum + toInt(b[field]), 0);
    const pick = (field) =>
      deviceData?.[field] !== undefined && deviceData?.[field] !== null
        ? toInt(deviceData[field])
        : sumBranches(field);
    const online = pick('total_online');
    const offline = pick('total_offline');
    const totalDevices = pick('total_devices');

    const customer = customerRes.status === 'fulfilled' ? customerRes.value.data?.data : null;
    const withDevice = toInt(customer?.with_device);
    const withoutDevice = toInt(customer?.without_device);
    const totalCustomers = toInt(customer?.total_customers) || withDevice + withoutDevice;

    const warning = warningRes.status === 'fulfilled' ? warningRes.value.data?.data : null;

    setStats({
      devices: totalDevices,
      // Online/offline share is measured against connected devices, not all registered ones.
      connected: online + offline,
      online,
      offline,
      customers: totalCustomers,
      withDevice,
      warnings: toInt(warning?.total_warnings),
      unresolved: toInt(warning?.unresolved_warnings)
    });
    setLoading(false);
  };

  useEffect(() => {
    fetchStatistics();
    // Auto refresh every 60 seconds (silent, no skeleton flash)
    const interval = setInterval(fetchStatistics, 60000);
    return () => clearInterval(interval);
  }, []);

  if (loading || !stats) {
    return (
      <div className="kpi-grid">
        {Array.from({ length: 6 }).map((_, index) => (
          <div className="kpi" key={index}>
            <Skeleton active title={false} paragraph={{ rows: 3 }} />
          </div>
        ))}
      </div>
    );
  }

  const cards = [
    {
      key: 'devices',
      label: 'Tổng thiết bị',
      value: stats.devices,
      unit: 'thiết bị',
      icon: 'cpu',
      tone: 'blue',
      chip: 'Đang quản lý'
    },
    {
      key: 'online',
      label: 'Đang kết nối',
      value: stats.online,
      unit: 'online',
      icon: 'wifi',
      tone: 'green',
      chip: `${percent(stats.online, stats.connected)}% hoạt động`,
      onClick: () => filterDeviceList('online')
    },
    {
      key: 'offline',
      label: 'Mất kết nối',
      value: stats.offline,
      unit: 'offline',
      icon: 'wifi-off',
      tone: 'red',
      chip: 'Cần kiểm tra',
      onClick: () => filterDeviceList('offline')
    },
    {
      key: 'customers',
      label: 'Khách hàng',
      value: stats.customers,
      unit: 'khách hàng',
      icon: 'users',
      tone: 'violet',
      chip: 'Tổng khách hàng'
    },
    {
      key: 'installed',
      label: 'Đã lắp đặt',
      value: stats.withDevice,
      unit: 'khách hàng',
      icon: 'home',
      tone: 'cyan',
      chip: `${percent(stats.withDevice, stats.customers)}% khách hàng`
    },
    {
      key: 'warnings',
      label: 'Cảnh báo hôm nay',
      value: stats.warnings,
      unit: 'cảnh báo',
      icon: 'bell',
      tone: 'orange',
      chip: `${formatCurrency(stats.unresolved, 0)} chưa xử lý`,
      onClick: scrollToTriage
    }
  ];

  return (
    <div className="kpi-grid">
      {cards.map((card) => (
        <div className={`kpi kpi--${card.tone}`} key={card.key}>
          <div className="kpi__top">
            <span className="kpi__label">{card.label}</span>
            <span className="kpi__icon">
              <LineIcon name={card.icon} size={16} />
            </span>
          </div>
          <div className="kpi__value">
            {formatCurrency(card.value, 0)}
            <small>{card.unit}</small>
          </div>
          {card.onClick ? (
            <button type="button" className="kpi__chip is-action" onClick={card.onClick}>
              <span>{card.chip}</span>
              <LineIcon name="arrow-up-right" size={13} />
            </button>
          ) : (
            <span className="kpi__chip">
              <span>{card.chip}</span>
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

export default StatisticsCards;
